import { Option } from '../../../../../models/ProviderSearch';
import { ReverseCoverageTypesCodes } from '../../../../Constants';
import { ConstantsLagoon } from '../../../../ConstantsLagoon';

export interface BhAoeItem {
  label: string;
  value: string;
  dnt_label: string;
}

export interface BhAoeCategory {
  category: string;
  items: BhAoeItem[];
}

const AOE_CATEGORY_LABELS = [
  'CONTENT_FILTERS.AREAS_OF_EXPERTISE_SUBCATEGORIES.POPULAR_SEARCHES',
  'CONTENT_FILTERS.AREAS_OF_EXPERTISE_SUBCATEGORIES.LIFESTYLE_AND_WELLNESS',
  'CONTENT_FILTERS.AREAS_OF_EXPERTISE_SUBCATEGORIES.IDENTITY',
  'CONTENT_FILTERS.AREAS_OF_EXPERTISE_SUBCATEGORIES.SPECIFIC_CONCERNS',
];

// Single source of truth: grouped codes mapped to their label
const aoeGroups: Record<string, string[]> = {
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.DEPRESSION_OR_MOOD_DISORDERS': [
    'E070',
    'E165',
  ],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.EATING_DISORDERS': [
    'E392',
    'A040',
    'SP40',
    'ED',
  ],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.FAMILY_THERAPY': ['A115', 'E397'],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.PARENTING': ['A110', 'A129'],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.ALCOHOL_AND_DRUG_USE': [
    'A020',
    'A021',
    'A075',
  ],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.ABUSE': ['E010', 'E425'],
  'CONTENT_FILTERS.GROUPED_AOE_OPTIONS.TRAUMA_OR_PTSD': ['E374', 'SP70'],
};

// Derived: code → comma-separated group codes
export const mapAoeCode: Record<string, string> = Object.values(
  aoeGroups
).reduce((acc, codes) => {
  const joined = codes.join(',');
  const [firstCode] = codes;
  if (firstCode) {
    acc[firstCode] = joined;
  }
  return acc;
}, {} as Record<string, string>);

// Derived: code → label (for grouped codes)
// Plus standalone code → label mappings
export const staticAoeCode: Record<string, string> = {
  ...Object.entries(aoeGroups).reduce((acc, [label, codes]) => {
    codes.forEach((code) => {
      acc[code] = label;
    });
    return acc;
  }, {} as Record<string, string>),
  // Standalone codes (not grouped)
  E025: 'CONTENT_FILTERS.AOE_CODES.E025',
  E020: 'CONTENT_FILTERS.AOE_CODES.E020',
  P020: 'CONTENT_FILTERS.AOE_CODES.P020',
  E115: 'CONTENT_FILTERS.AOE_CODES.E115',
  E145: 'CONTENT_FILTERS.AOE_CODES.E145',
  E060: 'CONTENT_FILTERS.AOE_CODES.E060',
  E160: 'CONTENT_FILTERS.AOE_CODES.E160',
  E180: 'CONTENT_FILTERS.AOE_CODES.E180',
  E507: 'CONTENT_FILTERS.AOE_CODES.E507',
  E225: 'CONTENT_FILTERS.AOE_CODES.E225',
  E230: 'CONTENT_FILTERS.AOE_CODES.E230',
  E030: 'CONTENT_FILTERS.AOE_CODES.E030',
  E050: 'CONTENT_FILTERS.AOE_CODES.E050',
  E120: 'CONTENT_FILTERS.AOE_CODES.E120',
  E110: 'CONTENT_FILTERS.AOE_CODES.E110',
  E479: 'CONTENT_FILTERS.AOE_CODES.E479',
  E472: 'CONTENT_FILTERS.AOE_CODES.E472',
  E162: 'CONTENT_FILTERS.AOE_CODES.E162',
  E017: 'CONTENT_FILTERS.AOE_CODES.E017',
  E390: 'CONTENT_FILTERS.AOE_CODES.E390',
  E042: 'CONTENT_FILTERS.AOE_CODES.E042',
  A143: 'CONTENT_FILTERS.AOE_CODES.A143',
  E373: 'CONTENT_FILTERS.AOE_CODES.E373',
  IDDA: 'CONTENT_FILTERS.AOE_CODES.IDDA',
  E185: 'CONTENT_FILTERS.AOE_CODES.E185',
  E210: 'CONTENT_FILTERS.AOE_CODES.E210',
  E015: 'CONTENT_FILTERS.AOE_CODES.E015',
  E445: 'CONTENT_FILTERS.AOE_CODES.E445',
};

export const staticAoeEnglishMap = {
  // Grouped AOE codes (using first code from each group)
  E070: 'Depression or mood disorders',
  E392: 'Eating disorders',
  A115: 'Family therapy',
  A110: 'Parenting',
  A020: 'Alcohol and drug use',
  E010: 'Abuse (emotional, sexual, physical)',
  E374: 'Trauma or PTSD',
  // Standalone AOE codes
  E015: 'Adoption',
  E445: 'Foster care',
  E025: 'Attention deficit disorders (ADHD)',
  E020: 'Anxiety or stress',
  P020: "Marriage & Couple's Therapy",
  E115: 'Grief',
  E145: 'Infertility',
  E060: 'Compulsive gambling',
  E160: 'Medication management',
  E180: 'Pain management',
  E507: 'Postpartum depression (PPD)',
  E225: 'Sex therapy',
  E230: 'Sleep disorders',
  E030: 'Weight loss',
  E050: 'Christian counseling',
  E120: 'Deafness and hearing loss',
  E110: 'LGBTQ+ identified clinician',
  E479: 'LGBTQ+ supportive',
  E472: 'Raced-based trauma',
  E162: 'Military & veterans',
  E017: 'Anger',
  E390: 'Autism spectrum disorders',
  E042: 'Bipolar disorder',
  A143: 'First responder',
  E373: 'Physical & mental disabilities',
  IDDA: 'Neurodiversity or development disability',
  E185: 'Personality disorders',
  E210: 'Schizophrenia and other psychotic disorders',
};

/**
 * Merges BH-specific AOE labels into `aoeTranslations` when the member is on a
 * Behavioral Health / Behavioral Carve-Out coverage type and the
 * ENABLE_BH_GROUPED_AOE_FILTER feature flag is enabled.
 * Mutates `aoeTranslations` in place.
 */
export const applyBhAoeTranslations = (
  aoeTranslations: any,
  coverageType: string,
  enableBhGroupedAoeFilter: boolean
): void => {
  const isBhGroupedAoeCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
  if (enableBhGroupedAoeFilter && isBhGroupedAoeCoverage) {
    Object.assign(aoeTranslations, staticAoeEnglishMap);
  }
};

const getAoeCategoryKeys = (): string[] => {
  const config = ConstantsLagoon?.CONFIG || {};

  return [
    config.AOE_CODE_POPULAR_SEARCHES,
    config.AOE_CODE_LIFESTYLE_WELLNESS,
    config.AOE_CODE_IDENTITY,
    config.AOE_CODE_SPECIFIC_CONCERNS,
  ];
};

const parseLagoonConfigArray = (
  configValue: string | null | undefined
): string[] =>
  configValue
    ? configValue
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    : [];

const getLagoonConfigValue = (allConfigs: any, key: string): string => {
  const config = allConfigs?.(key);
  return config?.value || '';
};

const getAoeCategoryMaps = (allConfigs: any): Record<string, string>[] =>
  getAoeCategoryKeys().map((key) => {
    const lagoonCodes = getLagoonConfigValue(allConfigs, key);
    const codes = parseLagoonConfigArray(lagoonCodes);
    return codes.reduce<Record<string, string>>((acc, code) => {
      //create objects  {a1:a1},{a2:a2},,}
      acc[code] = code;
      return acc;
    }, {});
  });

export const buildBhGroupedAoeData = (
  apiOptions: Option[] = [],

  allConfigs: any
): BhAoeCategory[] => {
  const cat1Items: BhAoeItem[] = [];
  const cat2Items: BhAoeItem[] = [];
  const cat3Items: BhAoeItem[] = [];
  const cat4Items: BhAoeItem[] = [];
  const categoryItems = [cat1Items, cat2Items, cat3Items, cat4Items];

  const [cat1Map, cat2Map, cat3Map, cat4Map] = getAoeCategoryMaps(allConfigs);
  for (const option of apiOptions) {
    const code = String(option.value);
    const item: BhAoeItem = {
      label: String(option.label),
      value: code,
      dnt_label: String(option.label),
    };

    if (cat1Map[code]) cat1Items.push(item);
    if (cat2Map[code]) cat2Items.push(item);
    if (cat3Map[code]) cat3Items.push(item);
    if (cat4Map[code]) cat4Items.push(item);
  }

  categoryItems.forEach((items) =>
    items.sort((a, b) => a.label.localeCompare(b.label))
  );

  return AOE_CATEGORY_LABELS.map((category, index) => ({
    category,
    items: categoryItems[index],
  })).filter((entry) => entry.items.length > 0);
};
// string building
export const buildBhGroupedAoeCategoriesCode = (
  selectedValues: string[],
  allConfigs: any
): string => {
  const [cat1Map, cat2Map, cat3Map, cat4Map] = getAoeCategoryMaps(allConfigs);

  let cat1 = '';
  let cat2 = '';
  let cat3 = '';
  let cat4 = '';

  for (const value of selectedValues) {
    const code = String(value);
    const mapCode = mapAoeCode[code];
    const formattedCode = mapCode || code;
    if (cat1Map[code]) cat1 = cat1 ? `${cat1},${formattedCode}` : formattedCode;
    if (cat2Map[code]) cat2 = cat2 ? `${cat2},${formattedCode}` : formattedCode;
    if (cat3Map[code]) cat3 = cat3 ? `${cat3},${formattedCode}` : formattedCode;
    if (cat4Map[code]) cat4 = cat4 ? `${cat4},${formattedCode}` : formattedCode;
  }

  return [cat1, cat2, cat3, cat4].filter(Boolean).join('|');
};
